import React, {
  useCallback, useState, useRef, useEffect,
} from 'react';
import propTypes from 'prop-types';

import { useAppStore } from '../../stores';

import Modal from '../Modal/Modal';
import PlaceDetail from '../Place/PlaceDetail';
import FavoriteButton from '../Personalization/Favorite/FavoriteButton';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

const PRIORITY_LOAD_COUNT = 6; // Load first 6 images immediately

const AmenitiesPageListItem = ( { item, index } ) => {
  const [ modalIsOpen, setModalIsOpen ] = useState( false );
  // Initialize with pre-built nav buttons from item data
  const [ modalNavBtns, setModalNavBtns ] = useState( item.navBtns || null );
  const [ imageLoaded, setImageLoaded ] = useState( false );
  const imgHolderRef = useRef( null );
  const placeholderRef = useRef( null );

  // Use shared IntersectionObserver, skip for first 6 items (priority loading)
  const isPriority = index < PRIORITY_LOAD_COUNT;
  const [containerRef, isVisible] = useIntersectionObserver(isPriority);

  const personalizationActive = useAppStore((state) => state.personalizationActive);

  // Update nav buttons when phase 2 data arrives with navBtns
  useEffect(() => {
    if (item.navBtns && item.navBtns !== modalNavBtns) {
      setModalNavBtns(item.navBtns);
    }
  }, [item.navBtns, modalNavBtns]);

  const {
    uuid,
    title,
    coverImage,
  } = item;

  // Only download actual image when item becomes visible
  // URLs are fetched in initial API call, but images don't download until here
  useEffect(() => {
    if (!isVisible || !coverImage || imageLoaded) return undefined;

    let isMounted = true;
    const img = new Image();

    img.onload = () => {
      if (!isMounted) return;
      setImageLoaded(true);
      if (imgHolderRef.current && placeholderRef.current) {
        placeholderRef.current.classList.add('imgHolder__placeholder--hidden');
        imgHolderRef.current.classList.add('imgHolder--loaded');
      }
    };

    img.onerror = () => {
      if (!isMounted) return;
      setImageLoaded(true);
      if (imgHolderRef.current && placeholderRef.current) {
        placeholderRef.current.classList.add('imgHolder__placeholder--hidden');
        imgHolderRef.current.classList.add('imgHolder--loaded');
      }
    };

    img.src = coverImage;

    return () => {
      isMounted = false;
      // Cancel image loading by clearing src
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [isVisible, coverImage, imageLoaded]);

  const openModal = useCallback( () => {
    setModalIsOpen( true );
  }, [] );

  const components = personalizationActive ? [ <FavoriteButton
    key={`favorite-${uuid}`}
    type="place"
    uuid={uuid}
    hasShadow={false}
    className="detailHeader__favorite"
  /> ] : [];

  const headerData = {
    components,
  };

  return (
    <>
      <Modal
        modalClassName="modal--anchored"
        isOpen={modalIsOpen}
        handleModalHide={() => setModalIsOpen( false )}
        showBlur
        showCloseButton
        title={title}
        headerData={headerData}
        navBtns={modalNavBtns}
      >

        <PlaceDetail
          uuid={item.photoGalleryImages?.length > 0 ? null : uuid}
          data={item}
          setModalNavBtns={setModalNavBtns}
          parent="modal"
        />

      </Modal>

      <div className="gridItem" ref={containerRef}>
        <button
          className="modalOpen gridItemTrigger"
          onClick={openModal}
          type="button"
        >
          <div>
            <div className="imgContainer">
              {coverImage && <div ref={placeholderRef} className="imgHolder__placeholder" />}
              <div
                ref={imgHolderRef}
                className="imgHolder"
                style={{
                  backgroundImage: coverImage ? `url(${coverImage})` : 'none',
                }}
              />
              <h2 className="gridItemTitle">{title}</h2>
            </div>
          </div>
        </button>

        { personalizationActive && (
          <FavoriteButton
            type="place"
            uuid={uuid}
            hasShadow
            className="favoriteBtn-positioner"
          />
        )}

      </div>
    </>
  );
};

AmenitiesPageListItem.propTypes = {
  item: propTypes.shape({
    uuid: propTypes.string,
    title: propTypes.string,
    coverImage: propTypes.string,
    description: propTypes.string,
    photoGalleryImages: propTypes.arrayOf(propTypes.shape({
      id: propTypes.string,
      image: propTypes.string,
      caption: propTypes.string,
    })),
    video: propTypes.shape({
      service: propTypes.string,
      serviceVideoId: propTypes.string,
    }),
    threeSixties: propTypes.arrayOf(propTypes.shape({
      uuid: propTypes.string,
      type: propTypes.string,
      buttonText: propTypes.string,
      url: propTypes.string,
    })),
    navBtns: propTypes.arrayOf(propTypes.shape({
      section: propTypes.string,
      text: propTypes.string,
    })),
  }).isRequired,
  index: propTypes.number.isRequired,
};

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(AmenitiesPageListItem);
